import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { io } from 'socket.io-client'
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiArrowLeft,
  FiSave,
  FiLoader,
  FiEye,
  FiX,
  FiChevronUp,
  FiChevronDown,
  FiTrendingUp,
  FiStar,
  FiCreditCard,
  FiBell,
  FiMessageCircle,
  FiPackage,
  FiRefreshCw,
  FiUsers
} from 'react-icons/fi'
import axios from 'axios'
import toast from 'react-hot-toast'
import CreateWebsiteModal from '../../components/websites/CreateWebsiteModal'
import WebsiteEditorModal from '../../components/websites/WebsiteEditorModal'
import SectionEditorModal from '../../components/websites/SectionEditorModal'
import PaymentSettingsModal from '../../components/websites/PaymentSettingsModal'
import PaymentConfigModal from '../../components/websites/PaymentConfigModal'
import BankDetailsModal from '../../components/websites/BankDetailsModal'
import TelegramSettingsModal from '../../components/websites/TelegramSettingsModal'
import CryptoWalletConfigModal from '../../components/websites/CryptoWalletConfigModal'
import HeaderStylingModal from '../../components/websites/HeaderStylingModal'
import FooterStylingModal from '../../components/websites/FooterStylingModal'
import SectionFormStylingModal from '../../components/websites/SectionFormStylingModal'

const WEBSITE_CATEGORIES = [
  { value: 'music-artist', label: 'Music Artist', icon: '🎵' },
  { value: 'ecommerce', label: 'E-Commerce', icon: '🛍️' },
  { value: 'football-team', label: 'Football Team', icon: '⚽' },
  { value: 'service-based', label: 'Services', icon: '🔧' },
  { value: 'portfolio', label: 'Portfolio', icon: '🎨' },
  { value: 'business', label: 'Business', icon: '🏢' },
  { value: 'blog', label: 'Blog/News', icon: '📝' },
  { value: 'real-estate', label: 'Real Estate', icon: '🏠' },
  { value: 'restaurant', label: 'Restaurant', icon: '🍽️' },
  { value: 'educational', label: 'Education', icon: '🎓' },
  { value: 'non-profit', label: 'Non-Profit', icon: '❤️' },
  { value: 'event', label: 'Event', icon: '🎉' },
  { value: 'forum', label: 'Community/Forum', icon: '💬' },
  { value: 'job-board', label: 'Job Board', icon: '💼' },
  { value: 'booking', label: 'Booking', icon: '📅' },
  { value: 'directory', label: 'Directory', icon: '📍' }
]

export default function WebsiteBuilder() {
  const navigate = useNavigate()
  const { websiteId } = useParams()
  
  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditorModal, setShowEditorModal] = useState(false)
  const [showSectionModal, setShowSectionModal] = useState(false)
  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const [showPaymentConfigModal, setShowPaymentConfigModal] = useState(false)
  const [showTelegramModal, setShowTelegramModal] = useState(false)
  const [showWalletConfigModal, setShowWalletConfigModal] = useState(false)
  const [showHeaderStylingModal, setShowHeaderStylingModal] = useState(false)
  const [showFooterStylingModal, setShowFooterStylingModal] = useState(false)
  const [showSectionFormStylingModal, setShowSectionFormStylingModal] = useState(false)
  const [showBankDetailsModal, setShowBankDetailsModal] = useState(false)
  
  // Websites list
  const [websites, setWebsites] = useState([])
  const [loading, setLoading] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(6)
  const [totalWebsites, setTotalWebsites] = useState(0)
  
  // Current editing
  const [currentWebsite, setCurrentWebsite] = useState(null)
  const [currentSection, setCurrentSection] = useState(null)
  const [editingMode, setEditingMode] = useState(null) // 'create', 'edit', 'section'
  
  // Loading states
  const [savingWebsite, setSavingWebsite] = useState(false)
  const [deletingWebsiteId, setDeletingWebsiteId] = useState(null)
  const [publishingWebsiteId, setPublishingWebsiteId] = useState(null)
  const [extraPremiumLoadingId, setExtraPremiumLoadingId] = useState(null)
  const [togglingDefaultStylingId, setTogglingDefaultStylingId] = useState(null)
  const [appPremiumMode, setAppPremiumMode] = useState(true)
  const [socket, setSocket] = useState(null)
  const [draggedPageItem, setDraggedPageItem] = useState(null)
  const [pageDropIndex, setPageDropIndex] = useState(null)

  // Setup socket connection and listeners for real-time updates
  useEffect(() => {
    const token = localStorage.getItem('token')
    const isLocalhost = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
    const backendUrl = isLocalhost
      ? 'http://localhost:5000'
      : 'https://marketbooksolutionbackendmain.onrender.com'

    const newSocket = io(backendUrl, {
      auth: {
        token: token
      },
      path: '/socket.io',
      // Improved socket configuration with better timeout and reconnection handling
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 10,
      timeout: 30000, // 30 second connection timeout instead of default 20
      connectTimeout: 30000,
      ackTimeout: 30000,
      retryInterval: 1000,
      transports: ['websocket', 'polling']
    })
    setSocket(newSocket)

    // Join user room for receiving updates
    newSocket.on('connect', () => {
      console.log('[WebsiteBuilder] Socket connected:', newSocket.id)
      // Emit join-room event to join user's personal room
      if (token) {
        // Extract userId from token if possible, or use a derived identifier
        // The backend will use socket.user._id after jwt verification
        newSocket.emit('join-room', token)
      }
    })

    // Listen for extra premium updates
    newSocket.on('extra-premium-updated', (data) => {
      console.log('[WebsiteBuilder] Received extra-premium-updated event:', data)
      if (data.websiteId) {
        // Update the specific website with new premium state
        setWebsites((prevWebsites) =>
          prevWebsites.map((w) =>
            w._id === data.websiteId
              ? {
                  ...w,
                  extraPremium: data.website?.extraPremium || w.extraPremium
                }
              : w
          )
        )
        console.log(`[WebsiteBuilder] Updated website ${data.websiteId} with new premium state`)
      }
    })

    // Enhanced error handling
    newSocket.on('connect_error', (error) => {
      console.error('[WebsiteBuilder] Socket connection error:', error.message || error)
    })

    newSocket.on('error', (error) => {
      console.error('[WebsiteBuilder] Socket error:', error)
    })

    newSocket.on('disconnect', (reason) => {
      console.log('[WebsiteBuilder] Socket disconnected:', reason)
      if (reason === 'io server disconnect') {
        // Server disconnected, try to reconnect
        newSocket.connect()
      }
    })

    return () => {
      newSocket.disconnect()
    }
  }, [])

  useEffect(() => {
    fetchWebsites(1)
    fetchAppPremiumMode()
  }, [])

  // Removed automatic polling refresh - dashboard now only updates manually via refresh button
  // Socket listeners still provide real-time updates for premium state changes

  const fetchWebsites = async (page = 1) => {
    try {
      setLoading(true)
      const response = await axios.get('/websites', {
        params: {
          page,
          limit: itemsPerPage
        }
      })
      const data = response.data || {}
      let websitesData = data.websites || []
      setTotalWebsites(data.total || data.pagination?.total || 0)
      setCurrentPage(page)

      // Fetch actual order counts for each website
      websitesData = await Promise.all(
        websitesData.map(async (website) => {
          try {
            const ordersResponse = await axios.get(`/websites/${website._id}/orders?limit=1`)
            const orderCount = ordersResponse.data.pagination?.total || 0
            return {
              ...website,
              analytics: {
                ...website.analytics,
                totalOrders: orderCount
              }
            }
          } catch (error) {
            console.warn(`Failed to fetch order count for website ${website._id}:`, error)
            // Return website with original count if fetch fails
            return website
          }
        })
      )

      setWebsites(websitesData)
    } catch (error) {
      console.error('Error fetching websites:', error.response?.data || error.message || error)

      if (!error.response && error.message === 'Network Error') {
        // Retry using a fully resolved backend URL in case the baseURL was not reachable
        try {
          const backendBase = axios.defaults.baseURL || `${window.location.origin}/api`
          const retryUrl = backendBase.replace(/\/+$/, '') + '/websites'
          const retryResponse = await axios.get(retryUrl, {
            params: {
              page,
              limit: itemsPerPage
            }
          })
          const retryData = retryResponse.data || {}
          let websitesRetryData = retryData.websites || []
          setTotalWebsites(retryData.total || retryData.pagination?.total || 0)
          setCurrentPage(page)

          websitesRetryData = await Promise.all(
            websitesRetryData.map(async (website) => {
              try {
                const ordersResponse = await axios.get(`${retryUrl}/${website._id}/orders?limit=1`)
                const orderCount = ordersResponse.data.pagination?.total || 0
                return {
                  ...website,
                  analytics: {
                    ...website.analytics,
                    totalOrders: orderCount
                  }
                }
              } catch (retryOrderError) {
                console.warn(`Failed to fetch order count for website ${website._id} on retry:`, retryOrderError)
                return website
              }
            })
          )

          setWebsites(websitesRetryData)
          return
        } catch (retryError) {
          console.error('Retry after network error failed:', retryError.response?.data || retryError.message || retryError)
        }
      }

      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Unauthorized access. Please login again.')
      } else {
        toast.error('Failed to load websites')
      }
    } finally {
      setLoading(false)
    }
  }

  const fetchAppPremiumMode = async () => {
    try {
      const response = await axios.get('/global-admin/public/premium-mode')
      setAppPremiumMode(response.data.appPremiumMode)
    } catch (error) {
      console.error('Error fetching app premium mode:', error)
      setAppPremiumMode(true)
    }
  }

  const handleToggleExtraPremium = async (website) => {
    if (appPremiumMode) {
      toast.success('App is currently in Premium Mode. All premium sections are available automatically.')
      return
    }

    setExtraPremiumLoadingId(website._id)
    try {
      const currentStatus = website.extraPremium?.requestStatus
      const isActiveOrPending = currentStatus === 'approved' || currentStatus === 'pending'
      const enable = !isActiveOrPending
      const response = await axios.post(`/websites/${website._id}/extra-premium/toggle`, { enable })
      const updatedWebsite = response.data.website
      setWebsites(websites.map(w => w._id === website._id ? updatedWebsite : w))
      toast.success(response.data.message)
    } catch (error) {
      console.error('Error toggling Extra Premium Layer:', error)
      toast.error(error.response?.data?.message || 'Failed to update Extra Premium Layer request')
    } finally {
      setExtraPremiumLoadingId(null)
    }
  }

  const handleToggleDefaultStyling = async (website) => {
    setTogglingDefaultStylingId(website._id)
    try {
      const newDefaultStylingState = !website.useDefaultStyling
      const response = await axios.post(`/websites/${website._id}/styling/toggle-default`, { 
        useDefault: newDefaultStylingState 
      })
      const updatedWebsite = { ...website, useDefaultStyling: newDefaultStylingState }
      setWebsites(websites.map(w => w._id === website._id ? updatedWebsite : w))
      toast.success(newDefaultStylingState ? 'Switched to default design' : 'Switched to custom design')
    } catch (error) {
      console.error('Error toggling default styling:', error)
      toast.error(error.response?.data?.message || 'Failed to toggle design')
    } finally {
      setTogglingDefaultStylingId(null)
    }
  }

  const isCryptoFeatureEnabled = (website) => {
    const approvedCryptoProduct = (website.extraPremium?.isActive || website.extraPremium?.requestStatus === 'approved') && website.extraPremium?.allowedSections?.cryptoProduct
    return !appPremiumMode || approvedCryptoProduct
  }

  const getVisibleSections = (website) => {
    if (!website) return []
    if (Array.isArray(website.sections)) return website.sections
    if (Array.isArray(website.visibleSections)) return website.visibleSections
    return []
  }

  const getPageItems = (website) => {
    const sections = getVisibleSections(website)
    const headerOrder = Number.isInteger(website?.headerOrder)
      ? Math.max(0, Math.min(website.headerOrder, sections.length))
      : 0
    const pageItems = [...sections]
    pageItems.splice(headerOrder, 0, { _id: '__system-header__', isSystemHeader: true, title: 'Header' })
    return pageItems
  }

  const handleCreateWebsite = async (websiteData) => {
    // Directly create the website from CreateWebsiteModal data
    setSavingWebsite(true)
    try {
      // Validate required fields
      if (!websiteData.name || !websiteData.name.trim()) {
        toast.error('Website name is required')
        return
      }

      if (!websiteData.category || !websiteData.category.trim()) {
        toast.error('Website category is required')
        return
      }

      console.log('Creating website with data:', websiteData)

      // Create the website with just name and category
      console.log('Sending POST request with data:', { name: websiteData.name, category: websiteData.category })
      const response = await axios.post('/websites', {
        name: websiteData.name,
        category: websiteData.category
      })
      
      console.log('Website creation response:', response.data)
      
      if (!response.data) {
        throw new Error('No response data received from server')
      }

      if (!response.data.website) {
        console.error('Response missing website object:', response.data)
        throw new Error('Server did not return created website data')
      }
      
      // Add newly created website to list
      setWebsites([response.data.website, ...websites])
      toast.success('Website created successfully!')
      setShowCreateModal(false)
      
      // Fetch fresh list to ensure we have all sections populated
      await fetchWebsites()
    } catch (error) {
      console.error('Error creating website:', error)
      console.error('Full error object:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText
      })
      
      let errorMessage = 'Failed to create website'
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error
      } else if (error.message) {
        errorMessage = error.message
      }
      
      console.error('Final error message displayed to user:', errorMessage)
      toast.error(errorMessage)
      throw error // Re-throw so modal knows it failed
    } finally {
      setSavingWebsite(false)
    }
  }

  const handleSaveWebsite = async (websiteData) => {
    setSavingWebsite(true)
    try {
      // Validate required fields
      if (!websiteData.name || !websiteData.name.trim()) {
        toast.error('Website name is required')
        setSavingWebsite(false)
        return
      }

      if (!currentWebsite || !currentWebsite._id) {
        toast.error('Invalid website - please select a website to edit')
        setSavingWebsite(false)
        return
      }

      console.log('Updating website with data:', websiteData)
      console.log('Current website ID:', currentWebsite._id)

      // Update existing website - remove any category field from submitData
      // as category is not editable after creation
      const { category, ...updateData } = websiteData
      const response = await axios.put(`/websites/${currentWebsite._id}`, updateData)
      
      if (response.data && response.data.website) {
        setWebsites(websites.map(w => w._id === currentWebsite._id ? response.data.website : w))
        setCurrentWebsite(response.data.website)
        toast.success('Website updated successfully')
      }
      
      setShowEditorModal(false)
      fetchWebsites()
    } catch (error) {
      console.error('Error saving website:', error)
      console.error('Error response:', error.response?.data)
      const errorMessage = error.response?.data?.message || error.message || 'Failed to save website'
      toast.error(errorMessage)
    } finally {
      setSavingWebsite(false)
    }
  }

  const handlePublishWebsite = async (websiteId) => {
    setPublishingWebsiteId(websiteId)
    try {
      const response = await axios.put(`/websites/${websiteId}/publish`, {
        publish: true
      })
      setWebsites(prevWebsites => prevWebsites.map(w => w._id === websiteId ? response.data.website : w))
      toast.success('Website published successfully')
    } catch (error) {
      console.error('Error publishing website:', error)
      toast.error(error.response?.data?.message || 'Failed to publish website')
    } finally {
      setPublishingWebsiteId(null)
    }
  }

  const handleUnpublishWebsite = async (websiteId) => {
    setPublishingWebsiteId(websiteId)
    try {
      const response = await axios.put(`/websites/${websiteId}/unpublish`)
      setWebsites(prevWebsites => prevWebsites.map(w => w._id === websiteId ? response.data.website : w))
      toast.success('Website unpublished successfully')
    } catch (error) {
      console.error('Error unpublishing website:', error)
      toast.error(error.response?.data?.message || 'Failed to unpublish website')
    } finally {
      setPublishingWebsiteId(null)
    }
  }

  const handleRepublishWebsite = async (websiteId) => {
    setPublishingWebsiteId(websiteId)
    try {
      const response = await axios.put(`/websites/${websiteId}/republish`)
      setWebsites(websites.map(w => w._id === websiteId ? response.data.website : w))
      toast.success('Website republished successfully')
    } catch (error) {
      console.error('Error republishing website:', error)
      toast.error(error.response?.data?.message || 'Failed to republish website')
    } finally {
      setPublishingWebsiteId(null)
    }
  }

  const handleDeleteWebsite = async (websiteId) => {
    if (!window.confirm('Are you sure you want to delete this website? This action cannot be undone.')) {
      return
    }
    
    setDeletingWebsiteId(websiteId)
    try {
      await axios.delete(`/websites/${websiteId}`)
      setWebsites(websites.filter(w => w._id !== websiteId))
      toast.success('Website deleted successfully')
    } catch (error) {
      console.error('Error deleting website:', error)
      toast.error(error.response?.data?.message || 'Failed to delete website')
    } finally {
      setDeletingWebsiteId(null)
    }
  }

  const handleViewWebsite = (website) => {
    // Open public website in new tab
    window.open(`/store/${website.slug}`, '_blank')
  }

  const handleEditWebsite = (website) => {
    setCurrentWebsite(website)
    setEditingMode('edit')
    setShowEditorModal(true)
  }

  const handleLogoUpload = (updatedWebsite) => {
    // Update the current website with the uploaded logo data
    setCurrentWebsite(updatedWebsite)
    // Also update the websites list
    setWebsites(websites.map(w => w._id === updatedWebsite._id ? updatedWebsite : w))
    console.log('[WebsiteBuilder] Logo uploaded - updated website state')
  }

  const handleLogoDelete = (updatedWebsite) => {
    // Update the current website after logo deletion
    setCurrentWebsite(updatedWebsite)
    // Also update the websites list
    setWebsites(websites.map(w => w._id === updatedWebsite._id ? updatedWebsite : w))
    console.log('[WebsiteBuilder] Logo deleted - updated website state')
  }

  const handleEditSection = (section, website) => {
    setCurrentWebsite(website)
    setCurrentSection(section)
    setEditingMode('section')
    setShowSectionModal(true)
  }

  const handleSaveSection = async (sectionData) => {
    try {
      // Validate required data
      if (!currentWebsite._id) {
        console.error('Missing website ID')
        toast.error('Invalid website data')
        throw new Error('Missing website ID')
      }

      // Check if this is a NEW section or EXISTING section
      // Use editingMode as the definitive source of truth
      const isNewSection = editingMode === 'section' && !currentSection._id

      // Sanitize large data URLs (base64 images/audio) to avoid exceeding server body parser limits
      const sanitizeDataUrls = (obj, path = '') => {
        if (!obj || typeof obj !== 'object') return obj
        const cloned = Array.isArray(obj) ? [...obj] : { ...obj }
        for (const key of Object.keys(cloned)) {
          const val = cloned[key]
          const currentPath = path ? `${path}.${key}` : key
          if (typeof val === 'string' && val.startsWith('data:')) {
          // Allow larger files for:
          // 1. Audio files (up to 50MB base64) - audioFile URLs
          // 2. Team member images (up to 10MB)
          // 3. Hero/background/logo/gallery images (up to 8MB)
          // 4. Regular content images (250KB)
          
          const isAudioFile = currentPath.includes('audioFile') && key === 'url'
          const isTeamMemberImage = currentPath.includes('teamMembers') && key === 'url'
          const isLargeSectionImage = (
            currentPath.includes('heroImage') ||
            currentPath.includes('backgroundImage') ||
            currentPath.includes('backgroundVideo') ||
            currentPath.includes('logo') ||
            currentPath.includes('gallery') ||
            currentPath.includes('specialMedia') ||
            currentPath.includes('heroImages') ||
            currentPath.includes('leftSidebar') ||
            currentPath.includes('rightSidebar') ||
            currentPath.includes('items')
          ) && key === 'url'
          
          // Set max length based on file type
          let maxLength = 250000 // Default 250KB for regular content
          if (isAudioFile) {
            maxLength = 50000000 // 50MB for audio files (base64 encoded)
          } else if (isTeamMemberImage) {
            maxLength = 10000000 // 10MB for team member images
          } else if (isLargeSectionImage) {
            maxLength = 1000000000 // 1GB for large section images/videos (unlimited)
          }

          if (val.length > maxLength) {
            console.warn(`[SANITIZE] ${key} at ${currentPath} exceeds ${maxLength} bytes (length: ${val.length}), truncating...`)
            cloned[key] = val.substring(0, 100) + '...'; // Truncate large data URLs to avoid server limits
          } else if (isAudioFile) {
            console.log(`[SANITIZE] Audio file preserved: ${currentPath} (${val.length} bytes)`)
          }

        }
        }
        return cloned
      }

      console.log('=== SECTION SAVE START ===')
      console.log('Current Website ID:', currentWebsite._id)
      console.log('Current Section ID:', currentSection._id || 'NEW')
      console.log('Editing Mode:', editingMode)
      console.log('Is new section:', isNewSection)
      console.log('Section data from modal:', sectionData)
      console.log('Current section full data:', {
        id: currentSection._id,
        title: currentSection.title,
        sectionType: currentSection.sectionType,
        status: currentSection.status,
        hasContent: !!currentSection.content,
        hasStyling: !!currentSection.styling,
        hasVisibility: !!currentSection.visibility,
        hasFeatures: !!currentSection.features
      })

      // Transform modal data to backend format
      // Modal sends: title, description, type/sectionType, disabled, displayOrder, content
      // Backend expects: title, content, styling, visibility, features, status, (optional) sectionType
      const updateData = {
        title: typeof sectionData.title === 'string' ? sectionData.title.trim() : '',
        titleAvailable: sectionData.titleAvailable !== false,
        enabled: sectionData.enabled === true,
        status: sectionData.enabled === false || sectionData.disabled === true
          ? 'disabled'
          : (sectionData.status || (currentSection.status === 'disabled'
            ? (currentWebsite.status === 'published' ? 'published' : 'draft')
            : (currentSection.status || 'draft')))
      }
      
      // ALWAYS send content - ensure CTA and other fields are properly included
      if (sectionData.content) {
        // Always send content with all fields
        updateData.content = {
          ...sectionData.content,
          // Ensure CTA fields are explicitly included (trim whitespace)
          ctaText: (sectionData.content.ctaText || '').trim(),
          ctaLink: (sectionData.content.ctaLink || '').trim()
        }
        
        console.log('[SAVE-CTA] ==========================================')
        console.log('[SAVE-CTA] Sending UPDATE content to backend:')
        console.log('[SAVE-CTA] CTA Text:', updateData.content.ctaText)
        console.log('[SAVE-CTA] CTA Link:', updateData.content.ctaLink)
        console.log('[SAVE-CTA] Full content keys:', Object.keys(updateData.content))
        console.log('[SAVE-CTA] Full content:', JSON.stringify(updateData.content, null, 2))
        console.log('[SAVE-CTA] ==========================================')
      }

      // Sanitize large data URLs for updates too
      if (updateData.content) {
        updateData.content = sanitizeDataUrls(updateData.content)
      }
      
      // Always send styling from the modal data
      if (sectionData.styling) {
        updateData.styling = sectionData.styling
      }
      
      // Only send visibility if the section has visibility configured
      if (currentSection.visibility && Object.keys(currentSection.visibility).length > 0) {
        updateData.visibility = currentSection.visibility
      }
      
      // Only send features if the section has features configured
      if (currentSection.features && Object.keys(currentSection.features).length > 0) {
        updateData.features = currentSection.features
      }

      // If the modal provided a sectionType and it differs from current, include it to update
      if (sectionData.sectionType) {
        const currentType = currentSection.sectionType || currentSection.type || null
        if (sectionData.sectionType !== currentType) {
          updateData.sectionType = sectionData.sectionType
          console.log('Including sectionType in updateData:', updateData.sectionType)
        }
      }
      
      console.log('Transformed update data:', {
        title: updateData.title,
        hasContent: !!updateData.content,
        hasStyling: !!updateData.styling,
        hasVisibility: !!updateData.visibility,
        hasFeatures: !!updateData.features,
        status: updateData.status
      })
      console.log('Full transformed data:', JSON.stringify(updateData, null, 2))
      
      // HANDLE NEW SECTION CREATION
      if (isNewSection) {
        console.log('Creating new section...')
        console.log('[DEBUG] currentWebsite:', currentWebsite)
        console.log('[DEBUG] currentWebsite._id:', currentWebsite?._id)
        console.log('[DEBUG] sectionData:', sectionData)
        
        if (!currentWebsite._id) {
          throw new Error('Website ID is missing')
        }
        
        if (!sectionData.sectionType) {
          toast.error('Please select a section type')
          throw new Error('Section type is required')
        }
        
        const createData = {
          title: typeof sectionData.title === 'string' ? sectionData.title.trim() : '',
          titleAvailable: sectionData.titleAvailable !== false,
          enabled: sectionData.enabled === true,
          sectionType: sectionData.sectionType,
          status: sectionData.disabled
            ? 'disabled'
            : (currentWebsite.status === 'published' ? 'published' : 'draft'),
          content: {
            ...sectionData.content,
            // Explicitly include CTA fields with trimmed values
            ctaText: (sectionData.content?.ctaText || '').trim(),
            ctaLink: (sectionData.content?.ctaLink || '').trim()
          },
          styling: sectionData.styling || {}
        }

        createData.content = sanitizeDataUrls(createData.content)
        
        console.log('[CREATE-CTA] ==========================================')
        console.log('[CREATE-CTA] Sending CREATE content to backend:')
        console.log('[CREATE-CTA] CTA Text:', createData.content.ctaText)
        console.log('[CREATE-CTA] CTA Link:', createData.content.ctaLink)
        console.log('[CREATE-CTA] Full content keys:', Object.keys(createData.content))
        console.log('[CREATE-CTA] Full content:', JSON.stringify(createData.content, null, 2))
        console.log('[CREATE-CTA] ==========================================')
        console.log('[DEBUG] POST endpoint:', `/website-sections/website/${currentWebsite._id}`)
        console.log('[DEBUG] POST data:', createData)
        console.log('[DEBUG] createData.content keys:', Object.keys(createData.content))
        console.log('[DEBUG] Has heroImage:', !!createData.content.heroImage)
        if (createData.content.heroImage) {
          console.log('[DEBUG] heroImage object:', {
            hasUrl: !!createData.content.heroImage.url,
            urlLength: createData.content.heroImage.url?.length || 0,
            urlPreview: createData.content.heroImage.url?.substring(0, 100) || 'N/A'
          })
        }
        
        try {
          const response = await axios.post(
            `/website-sections/website/${currentWebsite._id}`,
            createData
          )
          
          console.log('=== SECTION CREATE SUCCESS ===')
          console.log('Section create response:', response.data)
          console.log('Response section data:', {
            id: response.data.section?._id,
            title: response.data.section?.title,
            sectionType: response.data.section?.sectionType,
            hasContent: !!response.data.section?.content,
            contentKeys: Object.keys(response.data.section?.content || {}),
            hasHeroImage: !!response.data.section?.content?.heroImage,
            heroImageSize: response.data.section?.content?.heroImage?.url ? 'yes' : 'no'
          })
          
          if (response.data && response.data.section) {
            // Verification fetch to confirm the section was truly saved with heroImage
            console.log('[VERIFY] Fetching section to verify save...')
            try {
              const verifyResponse = await axios.get(`/website-sections/${response.data.section._id}`)
              console.log('[VERIFY] Section fetch response:', {
                id: verifyResponse.data._id,
                hasHeroImage: !!verifyResponse.data.content?.heroImage,
                heroImageUrl: !!verifyResponse.data.content?.heroImage?.url,
                urlLength: verifyResponse.data.content?.heroImage?.url?.length || 0
              })
            } catch (verifyError) {
              console.error('[VERIFY] Failed to fetch section:', verifyError.message)
            }
            
            toast.success('Section created successfully')
            setShowSectionModal(false)
            setCurrentSection(null)
            fetchWebsites()
          } else {
            throw new Error('No section data in response')
          }
        } catch (postError) {
          console.error('[POST ERROR] Status:', postError.response?.status)
          console.error('[POST ERROR] Data:', postError.response?.data)
          console.error('[POST ERROR] Message:', postError.message)
          throw postError
        }
        return
      }

      // HANDLE EXISTING SECTION UPDATE
      console.log('Sending PUT request to: /website-sections/' + currentSection._id)
      console.log('[UPDATE] updateData.content keys:', Object.keys(updateData.content || {}))
      console.log('[UPDATE] Has heroImage in updateData:', !!updateData.content?.heroImage)
      if (updateData.content?.heroImage) {
        console.log('[UPDATE] heroImage in updateData:', {
          hasUrl: !!updateData.content.heroImage.url,
          urlLength: updateData.content.heroImage.url?.length || 0,
          urlPreview: updateData.content.heroImage.url?.substring(0, 100) || 'N/A'
        })
      }
      
      // Use correct endpoint
      const response = await axios.put(
        `/website-sections/${currentSection._id}`,
        updateData
      )
      
      console.log('=== SECTION SAVE SUCCESS ===')
      console.log('Section update response:', response.data)
      console.log('Response content keys:', Object.keys(response.data.section?.content || {}))
      console.log('Response heroImage present:', !!response.data.section?.content?.heroImage)
      
      const updatedSection = response.data?.section ||
        response.data?.website?.sections?.find(s => s._id === currentSection._id) ||
        currentSection

      if (updatedSection) {
        setCurrentSection(updatedSection)
        toast.success('Section updated successfully')
        setShowSectionModal(false)
        fetchWebsites()
      } else {
        throw new Error('No section data in response')
      }
    } catch (error) {
      console.error('=== SECTION SAVE ERROR ===')
      console.error('Error object:', error)
      console.error('Error message:', error.message)
      console.error('Error response:', error.response?.data)
      console.error('Error status:', error.response?.status)
      console.error('Error statusText:', error.response?.statusText)
      console.error('Full error:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          data: error.config?.data
        }
      })
      
      let errorMessage = 'Failed to save section'
      if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        // Multiple validation errors
        errorMessage = error.response.data.errors.map(e => e.message).join(', ')
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error
      } else if (error.message) {
        errorMessage = error.message
      }
      
      console.error('Final error message displayed:', errorMessage)
      toast.error(errorMessage)
      // Re-throw so modal knows it failed
      throw error
    }
  }

  const handleReorderSections = async (websiteId, allSections, visibleSections, direction, visibleIndex) => {
    if (!Array.isArray(visibleSections) || visibleSections.length === 0) {
      toast.error('No visible sections to reorder')
      return
    }

    if (!Array.isArray(allSections) || allSections.length === 0) {
      toast.error('No sections available to reorder')
      return
    }

    const currentSection = visibleSections[visibleIndex]
    if (!currentSection || !currentSection._id) {
      console.error('Section missing _id:', currentSection)
      toast.error('Invalid section data. Please refresh and try again.')
      return
    }

    const targetIndex = direction === 'up' ? visibleIndex - 1 : visibleIndex + 1
    if (targetIndex < 0 || targetIndex >= visibleSections.length) {
      return
    }

    const targetSection = visibleSections[targetIndex]
    if (!targetSection || !targetSection._id) {
      console.error('Target section missing _id:', targetSection)
      toast.error('Invalid target section data. Please refresh and try again.')
      return
    }

    const rawSections = [...allSections]
    const currentRawIndex = rawSections.findIndex(s => s._id === currentSection._id)
    const targetRawIndex = rawSections.findIndex(s => s._id === targetSection._id)

    if (currentRawIndex === -1 || targetRawIndex === -1) {
      console.error('Could not locate sections in raw sections list', { currentSection, targetSection, rawSections })
      toast.error('Invalid section ordering state. Please refresh and try again.')
      return
    }

    const reorderedSections = [...rawSections]
    ;[reorderedSections[currentRawIndex], reorderedSections[targetRawIndex]] = [reorderedSections[targetRawIndex], reorderedSections[currentRawIndex]]

    try {
      const sectionsData = reorderedSections.map((s, idx) => ({
        sectionId: s._id,
        order: idx
      }))

      console.log('Reordering sections:', sectionsData)

      const response = await axios.put(`/websites/${websiteId}/reorder-sections`, {
        sections: sectionsData
      })

      if (response.data && response.data.website) {
        setWebsites(websites.map(w => w._id === websiteId ? response.data.website : w))
        toast.success('Sections reordered')
      }
    } catch (error) {
      console.error('Error reordering sections:', error)
      console.error('Error response:', error.response?.data)
      const errorMessage = error.response?.data?.message || error.message || 'Failed to reorder sections'
      toast.error(errorMessage)
    }
  }

  const handleReorderHeader = async (website, direction) => {
    const sections = getVisibleSections(website)
    const allSections = Array.isArray(website.sections) ? website.sections : sections
    const currentOrder = Number.isInteger(website.headerOrder) ? website.headerOrder : 0
    const nextOrder = direction === 'up' ? currentOrder - 1 : currentOrder + 1
    if (nextOrder < 0 || nextOrder > sections.length) return

    try {
      const response = await axios.put(`/websites/${website._id}/reorder-sections`, {
        sections: allSections.map((section, index) => ({ sectionId: section._id, order: index })),
        headerOrder: nextOrder
      })
      if (response.data?.website) {
        setWebsites(websites.map((item) => item._id === website._id ? response.data.website : item))
        toast.success('Header position updated')
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to move header')
    }
  }

  const handlePageItemDrop = async (website, targetIndex) => {
    if (!draggedPageItem || draggedPageItem.websiteId !== website._id) return
    const pageItems = getPageItems(website)
    const sourceIndex = draggedPageItem.index
    if (sourceIndex === targetIndex || sourceIndex < 0 || targetIndex < 0 || targetIndex > pageItems.length) return

    const reorderedPageItems = [...pageItems]
    const [movedItem] = reorderedPageItems.splice(sourceIndex, 1)
    const insertionIndex = sourceIndex < targetIndex ? targetIndex - 1 : targetIndex
    reorderedPageItems.splice(insertionIndex, 0, movedItem)

    const orderedVisibleSections = reorderedPageItems.filter(item => !item.isSystemHeader)
    const visibleIds = new Set(orderedVisibleSections.map(section => String(section._id)))
    const hiddenSections = (website.sections || []).filter(section => !visibleIds.has(String(section._id)))
    const orderedSections = [...orderedVisibleSections, ...hiddenSections]
    const headerOrder = reorderedPageItems.findIndex(item => item.isSystemHeader)

    try {
      const response = await axios.put(`/websites/${website._id}/reorder-sections`, {
        sections: orderedSections.map((section, index) => ({ sectionId: section._id, order: index })),
        headerOrder
      })
      if (response.data?.website) {
        setWebsites(current => current.map(item => item._id === website._id ? response.data.website : item))
        toast.success('Page structure updated')
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save page structure')
    } finally {
      setDraggedPageItem(null)
      setPageDropIndex(null)
    }
  }

  const handleDeleteSection = async (websiteId, sectionId, sectionTitle, isHeroSection) => {
    if (isHeroSection) {
      toast.error('Hero section cannot be deleted')
      return
    }

    // Show confirmation dialog
    if (!window.confirm(`Are you sure you want to delete "${sectionTitle}"? This action cannot be undone.`)) {
      return
    }

    try {
      console.log(`Deleting section ${sectionId} from website ${websiteId}`)
      
      const response = await axios.delete(`/website-sections/${sectionId}`)
      
      if (response.data) {
        // Refresh the website data
        await fetchWebsites()
        toast.success('Section deleted successfully')
      }
    } catch (error) {
      console.error('Error deleting section:', error)
      console.error('Error response:', error.response?.data)
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete section'
      toast.error(errorMessage)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Websites</h1>
          <p className="text-gray-600 mt-1">Create and manage your online stores and websites</p>
        </div>
        <div className="flex flex-wrap gap-2 sm:gap-3">
          <button
            onClick={() => fetchWebsites(currentPage)}
            className="btn-secondary flex items-center gap-2"
            disabled={loading}
          >
            <FiRefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary flex items-center gap-2"
          >
            <FiPlus className="w-5 h-5" /> Create Website
          </button>
        </div>
      </div>

      {/* Free Mode Alert - Show when premium mode is disabled */}
      {!appPremiumMode && (
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
          <div className="flex">
            <div className="flex-shrink-0">
              <FiBell className="h-5 w-5 text-blue-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">Premium Features Restricted</h3>
              <div className="mt-2 text-sm text-blue-700">
                <p className="mb-2">
                  Access to the following sections for public published websites is currently restricted. To enable these premium features and enhance your website's capabilities, please contact the Global Admin or submit a support ticket to request pricing and upgrade options.
                </p>
                <div className="mt-2">
                  <p className="font-semibold mb-2">Restricted Sections:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Contact Us</li>
                    <li>Product</li>
                    <li>Fixtures</li>
                    <li>Resume</li>
                    <li>Points Table</li>
                    <li>Crypto Product</li>
                    <li>Customer Authentication</li>
                    <li>Ticket</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Loading state */}
      {loading && (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      )}

      {/* Empty state */}
      {!loading && websites.length === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
          <p className="text-gray-500 text-lg mb-4">No websites yet</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary inline-flex items-center gap-2"
          >
            <FiPlus className="w-5 h-5" /> Create Your First Website
          </button>
        </div>
      )}

      {/* Websites Grid */}
      {!loading && websites.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {websites.map((website) => (
            <div
              key={website._id}
              className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-lg transition-shadow overflow-hidden"
            >
              {/* Website Card Header */}
              <div className="bg-gradient-to-r from-primary-600 to-primary-700 p-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-white">{website.name}</h3>
                    <p className="text-primary-100 text-sm">
                      {WEBSITE_CATEGORIES.find(c => c.value === website.category)?.label}
                    </p>
                  </div>
                  <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    website.status === 'published' ? 'bg-green-100 text-green-800' :
                    website.status === 'draft' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {website.status.charAt(0).toUpperCase() + website.status.slice(1)}
                  </div>
                </div>
              </div>

              {/* Website Card Body */}
              <div className="p-4 space-y-4">
                {/* URL */}
                <div className="bg-gray-50 p-3 rounded border border-gray-200">
                  <p className="text-xs text-gray-600 uppercase tracking-wide">Public URL</p>
                  <p className="text-sm font-mono text-gray-900 break-all">{website.publicUrl}</p>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 sm:gap-3">
                  <div className="bg-gray-50 p-3 rounded text-center">
                    <p className="text-2xl font-bold text-primary-600">{getVisibleSections(website).length}</p>
                    <p className="text-xs text-gray-600 mt-1">Sections</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded text-center">
                    <p className="text-2xl font-bold text-blue-600">{website.analytics?.totalViews || 0}</p>
                    <p className="text-xs text-gray-600 mt-1">Views</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded text-center">
                    <p className="text-2xl font-bold text-green-600">{website.analytics?.totalOrders || 0}</p>
                    <p className="text-xs text-gray-600 mt-1">Orders</p>
                  </div>
                </div>

                {/* Extra Premium Layer */}
                {website.status === 'published' && (
                  <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mb-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                      <div>
                        <p className="text-sm font-semibold text-gray-900">Extra Premium Layer</p>
                        <p className="text-xs text-gray-600 mt-1">Request or manage access to advanced premium sections for this published website.</p>
                      </div>
                      <button
                        onClick={() => handleToggleExtraPremium(website)}
                        disabled={extraPremiumLoadingId === website._id}
                        className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${appPremiumMode ? 'bg-gray-200 text-gray-700 cursor-not-allowed' : website.extraPremium?.requestStatus === 'approved' || website.extraPremium?.requestStatus === 'pending' ? 'bg-green-600 text-white hover:bg-green-700' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
                      >
                        {extraPremiumLoadingId === website._id ? 'Saving...' : appPremiumMode ? 'Premium Mode Active' : (website.extraPremium?.requestStatus === 'approved' || website.extraPremium?.requestStatus === 'pending') ? 'Requested' : 'Request Premium'}
                      </button>
                    </div>
                    <div className="mt-3 text-sm text-gray-600 space-y-1">
                      {appPremiumMode ? (
                        <p>All premium sections are automatically available because the application is in Premium Mode.</p>
                      ) : website.extraPremium?.requestStatus === 'pending' ? (
                        <p className="text-amber-700">Request pending approval by Global Admin.</p>
                      ) : website.extraPremium?.requestStatus === 'approved' ? (
                        <p className="text-green-700">Request approved. Premium sections enabled based on Global Admin configuration.</p>
                      ) : website.extraPremium?.requestStatus === 'rejected' ? (
                        <p className="text-red-700">Previous request was rejected. Toggle again to submit a new request.</p>
                      ) : (
                        <p>Toggle this control to submit an Extra Premium Layer access request.</p>
                      )}
                    </div>
                  </div>
                )}

                {/* Sections List */}
                <div className="space-y-2">
                  <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Sections</p>
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {getPageItems(website).length > 0 ? (
                      getPageItems(website).map((section, idx) => section.isSystemHeader ? (
                        <div
                          key={section._id}
                          draggable
                          onDragStart={() => setDraggedPageItem({ websiteId: website._id, index: idx })}
                          onDragOver={(event) => { event.preventDefault(); setPageDropIndex(idx) }}
                          onDrop={(event) => { event.preventDefault(); handlePageItemDrop(website, idx) }}
                          onDragEnd={() => { setDraggedPageItem(null); setPageDropIndex(null) }}
                          className={`flex items-center justify-between p-2 rounded text-sm cursor-grab active:cursor-grabbing ${pageDropIndex === idx ? 'border-2 border-dashed border-blue-500 bg-blue-100' : 'bg-blue-50 border border-blue-200'}`}
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="text-gray-500 font-mono">{idx + 1}</span>
                            <span className="font-semibold text-blue-900">Header</span>
                            <span className="text-xs text-blue-700">System item</span>
                          </div>
                          <div className="flex gap-1">
                            <button
                              onClick={() => handleReorderHeader(website, 'up')}
                              disabled={idx === 0}
                              className="p-1 text-blue-600 hover:text-blue-900 disabled:opacity-30"
                              title="Move header up"
                            >
                              <FiChevronUp className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleReorderHeader(website, 'down')}
                              disabled={idx === getPageItems(website).length - 1}
                              className="p-1 text-blue-600 hover:text-blue-900 disabled:opacity-30"
                              title="Move header down"
                            >
                              <FiChevronDown className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div
                          key={section._id}
                          draggable={!section.isHeroSection}
                          onDragStart={() => setDraggedPageItem({ websiteId: website._id, index: idx })}
                          onDragOver={(event) => { event.preventDefault(); setPageDropIndex(idx) }}
                          onDrop={(event) => { event.preventDefault(); handlePageItemDrop(website, idx) }}
                          onDragEnd={() => { setDraggedPageItem(null); setPageDropIndex(null) }}
                          className={`flex items-center justify-between p-2 rounded text-sm ${section.isHeroSection ? '' : 'cursor-grab active:cursor-grabbing'} ${pageDropIndex === idx ? 'border-2 border-dashed border-blue-500 bg-blue-50' : 'bg-gray-50'}`}
                        >
                          <div className="flex-1 flex items-center gap-2 min-w-0">
                            <span className="text-gray-500 font-mono">{idx + 1}</span>
                            <span className="font-medium text-gray-900 truncate">{section.title}</span>
                            {section.isHeroSection && (
                              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded font-semibold">🎯 Hero</span>
                            )}
                            {(section.disabled === true || section.status === 'disabled') && (
                              <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">Disabled</span>
                            )}
                          </div>
                          <div className="flex gap-1">
                            <button
                              onClick={() => handleReorderSections(website._id, website.sections || [], getVisibleSections(website), 'up', idx - (Number.isInteger(website.headerOrder) && idx > website.headerOrder ? 1 : 0))}
                              disabled={idx === (Number.isInteger(website.headerOrder) ? website.headerOrder : 0) + 1 || section.isHeroSection}
                              className="p-1 text-gray-500 hover:text-gray-900 disabled:opacity-30"
                              title={section.isHeroSection ? "Hero section cannot be reordered" : "Move up"}
                            >
                              <FiChevronUp className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleReorderSections(website._id, website.sections || [], getVisibleSections(website), 'down', idx - (Number.isInteger(website.headerOrder) && idx > website.headerOrder ? 1 : 0))}
                              disabled={idx === getPageItems(website).length - 1 || section.isHeroSection}
                              className="p-1 text-gray-500 hover:text-gray-900 disabled:opacity-30"
                              title={section.isHeroSection ? "Hero section cannot be reordered" : "Move down"}
                            >
                              <FiChevronDown className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleEditSection(section, website)}
                              className="p-1 text-blue-600 hover:text-blue-900"
                              title="Edit section"
                            >
                              <FiEdit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteSection(website._id, section._id, section.title, section.isHeroSection)}
                              disabled={section.isHeroSection}
                              className="p-1 text-red-600 hover:text-red-900 disabled:opacity-30 disabled:cursor-not-allowed"
                              title={section.isHeroSection ? "Hero section cannot be deleted" : "Delete section"}
                            >
                              <FiTrash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-700">
                        No sections have been created yet. Add a section to start building your website.
                      </div>
                    )}
                    {draggedPageItem?.websiteId === website._id && getPageItems(website).length > 0 && (
                      <div
                        onDragOver={(event) => { event.preventDefault(); setPageDropIndex(getPageItems(website).length) }}
                        onDrop={(event) => { event.preventDefault(); handlePageItemDrop(website, getPageItems(website).length) }}
                        className={`h-8 flex items-center justify-center text-xs font-semibold text-blue-700 ${pageDropIndex === getPageItems(website).length ? 'border-2 border-dashed border-blue-500 bg-blue-50' : ''}`}
                      >
                        Drop item here
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      setCurrentWebsite(website)
                      setCurrentSection({ _id: null })  // Signal new section
                      setEditingMode('section')
                      setShowSectionModal(true)
                    }}
                    className="mt-2 w-full py-2 px-3 text-center bg-primary-50 text-primary-600 hover:bg-primary-100 rounded font-medium text-sm transition-colors border border-primary-200"
                  >
                    <FiPlus className="w-4 h-4 inline mr-1" /> Add Section
                  </button>
                </div>

                {/* Payment Status */}
                {website.paymentSettings?.acceptPayments && (
                  <div className="bg-green-50 border border-green-200 p-3 rounded flex items-center gap-2">
                    <FiCreditCard className="w-4 h-4 text-green-600" />
                    <span className="text-sm text-green-700 font-medium">Payments Enabled</span>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="pt-4 border-t border-gray-200">
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => navigate(`/customers/${website._id}`)}
                      className="inline-flex items-center justify-center gap-2 px-3 py-2 bg-slate-50 text-slate-700 hover:bg-slate-100 rounded font-medium text-sm transition-colors"
                    >
                      <FiUsers className="w-4 h-4" /> Customer Management
                    </button>
                  </div>

                  <div className="flex gap-2 mt-3 flex-wrap">
                  <button
                    onClick={() => handleViewWebsite(website)}
                    className="flex-1 min-w-max flex items-center justify-center gap-2 py-2 px-3 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded font-medium text-sm transition-colors"
                  >
                    <FiEye className="w-4 h-4" /> View
                  </button>
                  <button
                    onClick={() => handleEditWebsite(website)}
                    className="flex-1 min-w-max flex items-center justify-center gap-2 py-2 px-3 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded font-medium text-sm transition-colors"
                  >
                    <FiEdit2 className="w-4 h-4" /> Edit
                  </button>

                  <button
                    onClick={() => {
                      setCurrentWebsite(website)
                      setShowPaymentModal(true)
                    }}
                    className={`flex-1 min-w-max flex items-center justify-center gap-2 py-2 px-3 rounded font-medium text-sm transition-colors ${
                      website.paymentSettings?.acceptPayments
                        ? 'bg-green-50 text-green-600 hover:bg-green-100'
                        : 'bg-amber-50 text-amber-600 hover:bg-amber-100'
                    }`}
                  >
                    <FiCreditCard className="w-4 h-4" /> {website.paymentSettings?.acceptPayments ? 'Payment Settings' : 'Configure Payments'}
                  </button>

                  {/* Bank Details Button - Only show if Crypto Product is approved */}
                  {isCryptoFeatureEnabled(website) && (
                    <button
                      onClick={() => {
                        setCurrentWebsite(website)
                        setShowBankDetailsModal(true)
                      }}
                      className={`flex-1 min-w-max flex items-center justify-center gap-2 py-2 px-3 rounded font-medium text-sm transition-colors ${
                        website.paymentSettings?.bankName && website.paymentSettings?.accountName && website.paymentSettings?.accountNumber
                          ? 'bg-blue-50 text-blue-600 hover:bg-blue-100'
                          : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      🏦 {website.paymentSettings?.bankName ? 'Bank Details' : 'Setup Bank Details'}
                    </button>
                  )}

                  {/* Wallet Configuration Button - Only show if Crypto Product is approved */}
                  {isCryptoFeatureEnabled(website) && (
                    <button
                      onClick={() => {
                        setCurrentWebsite(website)
                        setShowWalletConfigModal(true)
                      }}
                      className={`flex-1 min-w-max flex items-center justify-center gap-2 py-2 px-3 rounded font-medium text-sm transition-colors ${
                        website.cryptoWalletConfig?.walletSource === 'manual'
                          ? 'bg-blue-50 text-blue-600 hover:bg-blue-100'
                          : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      💼 Wallet Configuration
                    </button>
                  )}

                  <button
                    onClick={() => {
                      setCurrentWebsite(website)
                      setShowHeaderStylingModal(true)
                    }}
                    className="flex-1 min-w-max flex items-center justify-center gap-2 py-2 px-3 rounded font-medium text-sm bg-violet-50 text-violet-600 hover:bg-violet-100 transition-colors"
                  >
                    🎨 Header Style
                  </button>

                  <button
                    onClick={() => {
                      setCurrentWebsite(website)
                      setShowFooterStylingModal(true)
                    }}
                    className="flex-1 min-w-max flex items-center justify-center gap-2 py-2 px-3 rounded font-medium text-sm bg-violet-50 text-violet-600 hover:bg-violet-100 transition-colors"
                  >
                    🎨 Footer Style
                  </button>

                  <button
                    onClick={() => {
                      setCurrentWebsite(website)
                      setShowSectionFormStylingModal(true)
                    }}
                    className="flex-1 min-w-max flex items-center justify-center gap-2 py-2 px-3 rounded font-medium text-sm bg-violet-50 text-violet-600 hover:bg-violet-100 transition-colors"
                  >
                    🎨 Section Form Style
                  </button>

                  {/* Design Toggle Button */}
                  <button
                    onClick={() => handleToggleDefaultStyling(website)}
                    disabled={togglingDefaultStylingId === website._id}
                    className={`flex-1 min-w-max flex items-center justify-center gap-2 py-2 px-3 rounded font-medium text-sm transition-colors ${
                      website.useDefaultStyling !== false
                        ? 'bg-blue-50 text-blue-600 hover:bg-blue-100 disabled:opacity-50'
                        : 'bg-orange-50 text-orange-600 hover:bg-orange-100 disabled:opacity-50'
                    }`}
                    title={website.useDefaultStyling !== false ? 'Using default design' : 'Using custom design'}
                  >
                    {togglingDefaultStylingId === website._id ? (
                      <span className="inline-block animate-spin">⚙️</span>
                    ) : (
                      <>
                        {website.useDefaultStyling !== false ? '✨ Default Design' : '🎨 Custom Design'}
                      </>
                    )}
                  </button>

                  {/* Crypto Transactions Button - Only show if Crypto Product is approved */}
                  {isCryptoFeatureEnabled(website) && (
                    <button
                      onClick={() => navigate(`/crypto-transactions/${website._id}`)}
                      className="flex-1 min-w-max flex items-center justify-center gap-2 py-2 px-3 bg-cyan-50 text-cyan-600 hover:bg-cyan-100 rounded font-medium text-sm transition-colors"
                    >
                      ₿ Crypto Transactions
                    </button>
                  )}

                  <button
                    onClick={() => {
                      setCurrentWebsite(website)
                      setShowTelegramModal(true)
                    }}
                    className={`flex-1 min-w-max flex items-center justify-center gap-2 py-2 px-3 rounded font-medium text-sm transition-colors ${
                      website.telegramConfig?.enabled
                        ? 'bg-sky-50 text-sky-600 hover:bg-sky-100'
                        : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <FiBell className="w-4 h-4" /> {website.telegramConfig?.enabled ? 'Telegram' : 'Setup Telegram'}
                  </button>

                  <button
                    onClick={() => navigate(`/tickets/${website._id}`)}
                    className="flex-1 min-w-max flex items-center justify-center gap-2 py-2 px-3 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded font-medium text-sm transition-colors"
                  >
                    🎫 Tickets
                  </button>

                  {website.sections && website.sections.find(s => s.sectionType === 'reviews' || s.type === 'reviews') && (
                    <button
                      onClick={() => navigate(`/reviews/${website._id}`)}
                      className="flex-1 min-w-max flex items-center justify-center gap-2 py-2 px-3 bg-yellow-50 text-yellow-700 hover:bg-yellow-100 rounded font-medium text-sm transition-colors"
                    >
                      <FiStar className="w-4 h-4" /> Reviews
                    </button>
                  )}

                  <button
                    onClick={() => navigate(`/payments-orders/${website._id}`)}
                    className="flex-1 min-w-max flex items-center justify-center gap-2 py-2 px-3 bg-emerald-50 text-emerald-600 hover:bg-emerald-100 rounded font-medium text-sm transition-colors"
                  >
                    <FiPackage className="w-4 h-4" /> Orders
                  </button>

                  <button
                    onClick={() => navigate(`/card-generator/${website._id}`)}
                    className="flex-1 min-w-max flex items-center justify-center gap-2 py-2 px-3 bg-indigo-50 text-indigo-600 hover:bg-indigo-100 rounded font-medium text-sm transition-colors"
                  >
                    💳 Cards
                  </button>

                  {website.status === 'published' ? (
                    <div className="flex flex-wrap gap-2 flex-1">
                      <button
                        onClick={() => handleUnpublishWebsite(website._id)}
                        disabled={publishingWebsiteId === website._id}
                        className="flex-1 min-w-max py-2 px-3 bg-slate-100 text-slate-700 hover:bg-slate-200 disabled:bg-gray-400 rounded font-medium text-sm transition-colors disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {publishingWebsiteId === website._id ? (
                          <>
                            <FiLoader className="w-4 h-4 animate-spin" /> Unpublishing
                          </>
                        ) : (
                          'Unpublish'
                        )}
                      </button>
                      <button
                        onClick={() => handleRepublishWebsite(website._id)}
                        disabled={publishingWebsiteId === website._id}
                        className="flex-1 min-w-max py-2 px-3 bg-amber-600 text-white hover:bg-amber-700 disabled:bg-gray-400 rounded font-medium text-sm transition-colors disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {publishingWebsiteId === website._id ? (
                          <>
                            <FiLoader className="w-4 h-4 animate-spin" /> Republishing
                          </>
                        ) : (
                          'Republish'
                        )}
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => handlePublishWebsite(website._id)}
                      disabled={publishingWebsiteId === website._id}
                      className="flex-1 min-w-max py-2 px-3 bg-green-600 text-white hover:bg-green-700 disabled:bg-gray-400 rounded font-medium text-sm transition-colors disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {publishingWebsiteId === website._id ? (
                        <>
                          <FiLoader className="w-4 h-4 animate-spin" /> Publishing
                        </>
                      ) : (
                        'Publish'
                      )}
                    </button>
                  )}
                  </div>

                  <button
                    onClick={() => handleDeleteWebsite(website._id)}
                    disabled={deletingWebsiteId === website._id}
                    className="py-2 px-3 bg-red-50 text-red-600 hover:bg-red-100 disabled:bg-gray-100 disabled:text-gray-400 rounded font-medium text-sm transition-colors disabled:cursor-not-allowed"
                  >
                    {deletingWebsiteId === website._id ? (
                      <FiLoader className="w-4 h-4 animate-spin" />
                    ) : (
                      <FiTrash2 className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {!loading && websites.length > 0 && totalWebsites > itemsPerPage && (
        <div className="flex items-center justify-between mt-8 px-4 py-4 border-t border-gray-200">
          <div className="text-sm text-gray-600">
            Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, totalWebsites)} of {totalWebsites} websites
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => fetchWebsites(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm"
            >
              Previous
            </button>
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.ceil(totalWebsites / itemsPerPage) }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  onClick={() => fetchWebsites(page)}
                  className={`px-3 py-2 rounded-lg font-medium text-sm ${
                    currentPage === page
                      ? 'bg-primary-600 text-white'
                      : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {page}
                </button>
              ))}
            </div>
            <button
              onClick={() => fetchWebsites(currentPage + 1)}
              disabled={currentPage >= Math.ceil(totalWebsites / itemsPerPage)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Modals */}
      {showCreateModal && (
        <CreateWebsiteModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateWebsite}
          categories={WEBSITE_CATEGORIES}
        />
      )}

      {showEditorModal && currentWebsite && (
        <WebsiteEditorModal
          isOpen={showEditorModal}
          onClose={() => {
            setShowEditorModal(false)
            setCurrentWebsite(null)
            setEditingMode(null)
          }}
          onSubmit={handleSaveWebsite}
          onLogoUpload={handleLogoUpload}
          onLogoDelete={handleLogoDelete}
          website={currentWebsite}
          categories={WEBSITE_CATEGORIES}
          isSaving={savingWebsite}
          mode={editingMode}
        />
      )}

      {showSectionModal && currentSection && currentWebsite && (
        <SectionEditorModal
          isOpen={showSectionModal}
          onClose={() => {
            setShowSectionModal(false)
            setCurrentSection(null)
          }}
          onSubmit={handleSaveSection}
          section={currentSection}
          website={currentWebsite}
        />
      )}

      {showPaymentModal && currentWebsite && (
        <PaymentSettingsModal
          isOpen={showPaymentModal}
          onClose={() => {
            setShowPaymentModal(false)
            setCurrentWebsite(null)
          }}
          website={currentWebsite}
          onSuccess={() => {
            setShowPaymentModal(false)
            setCurrentWebsite(null)
            fetchWebsites()
          }}
        />
      )}

      {showBankDetailsModal && currentWebsite && (
        <BankDetailsModal
          isOpen={showBankDetailsModal}
          onClose={() => {
            setShowBankDetailsModal(false)
            setCurrentWebsite(null)
          }}
          website={currentWebsite}
          onSuccess={() => {
            setShowBankDetailsModal(false)
            setCurrentWebsite(null)
            fetchWebsites()
          }}
        />
      )}

      {/* Payment Configuration Modal */}
      {showPaymentConfigModal && currentWebsite && (
        <PaymentConfigModal
          website={currentWebsite}
          onClose={() => {
            setShowPaymentConfigModal(false)
            setCurrentWebsite(null)
          }}
        />
      )}

      {/* Telegram Settings Modal */}
      {showTelegramModal && currentWebsite && (
        <TelegramSettingsModal
          isOpen={showTelegramModal}
          onClose={() => {
            setShowTelegramModal(false)
            setCurrentWebsite(null)
          }}
          website={currentWebsite}
          onSuccess={() => {
            setShowTelegramModal(false)
            setCurrentWebsite(null)
            fetchWebsites()
          }}
        />
      )}


      {showWalletConfigModal && currentWebsite && (
        <CryptoWalletConfigModal
          isOpen={showWalletConfigModal}
          onClose={() => {
            setShowWalletConfigModal(false)
            setCurrentWebsite(null)
          }}
          website={currentWebsite}
          onSuccess={() => {
            setShowWalletConfigModal(false)
            setCurrentWebsite(null)
            fetchWebsites()
          }}
        />
      )}

      {showHeaderStylingModal && currentWebsite && (
        <HeaderStylingModal
          isOpen={showHeaderStylingModal}
          onClose={() => {
            setShowHeaderStylingModal(false)
            setCurrentWebsite(null)
          }}
          website={currentWebsite}
          onSuccess={() => {
            setShowHeaderStylingModal(false)
            setCurrentWebsite(null)
            fetchWebsites()
          }}
        />
      )}

      {showFooterStylingModal && currentWebsite && (
        <FooterStylingModal
          isOpen={showFooterStylingModal}
          onClose={() => {
            setShowFooterStylingModal(false)
            setCurrentWebsite(null)
          }}
          website={currentWebsite}
          onSuccess={() => {
            setShowFooterStylingModal(false)
            setCurrentWebsite(null)
            fetchWebsites()
          }}
        />
      )}

      {showSectionFormStylingModal && currentWebsite && (
        <SectionFormStylingModal
          isOpen={showSectionFormStylingModal}
          onClose={() => {
            setShowSectionFormStylingModal(false)
            setCurrentWebsite(null)
          }}
          website={currentWebsite}
          onSuccess={() => {
            setShowSectionFormStylingModal(false)
            setCurrentWebsite(null)
            fetchWebsites()
          }}
        />
      )}
    </div>
  )
}


